/**
 * Uluslararası SEO: locale önekleri, çevrilmiş slug’lar, hreflang ve dil değişiminde URL üretimi.
 * İngilizce (en): kök URL’de önek yok (örn. /, /flights).
 * tr, de, … : /{locale}/… ve mümkün olan yerlerde yerelleştirilmiş segment.
 */
import { languages } from '@/lib/i18n';

export const HREFLANG_LOCALE_CODES = languages.map((l) => l.code);

const LANG_PREFIX_SET = new Set(HREFLANG_LOCALE_CODES);

/**
 * İngilizce segment → yerel slug (yalnızca App.js’de eşlenen rota + yönlendirme olanlar).
 * Diğer sayfalar için segment İngilizce kalır (/tr/products gibi).
 */
/** car-rentals kanonik segmentinin dil slug’ları. İngilizce kökte /car-rentals kalır. */
export const CAR_RENTAL_LOCALIZED_SLUG = {
  tr: 'arac-limousine-kiralanma',
  de: 'autovermietung-taxi-limousine',
  fr: 'location-voiture-taxi-limousine',
  es: 'alquiler-coches-taxi-limusina',
  it: 'noleggio-auto-taxi-limousine',
  pt: 'aluguel-carros-taxi-limusine',
  ru: 'arenda-avto-taksi-limuzin',
  zh: 'zuche-chuzu-limousine',
  ar: 'tajir-sayarat-taxi-limousine',
  fa: 'ejare-khodro-taxi-limuzin',
};

const LOCALIZED_SLUG = {
  tr: {
    flights: 'ucuslar',
    contact: 'iletisim',
    careers: 'kariyer',
    store: 'magaza',
    support: 'destek',
    about: 'hakkimizda',
    'refund-policy': 'iade-politikasi',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.tr,
  },
  de: {
    flights: 'fluege',
    contact: 'kontakt',
    store: 'shop',
    support: 'support',
    about: 'uber-uns',
    'refund-policy': 'ruckerstattung',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.de,
  },
  fr: {
    about: 'a-propos',
    'refund-policy': 'politique-de-remboursement',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.fr,
  },
  es: {
    about: 'sobre-nosotros',
    'refund-policy': 'politica-de-reembolso',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.es,
  },
  it: {
    about: 'chi-siamo',
    'refund-policy': 'politica-di-rimborso',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.it,
  },
  pt: {
    about: 'sobre-nos',
    'refund-policy': 'politica-de-reembolso',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.pt,
  },
  ru: {
    about: 'o-nas',
    'refund-policy': 'politika-vozvrata',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.ru,
  },
  zh: {
    about: 'guanyu-women',
    'refund-policy': 'tuikuan-zhengce',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.zh,
  },
  ar: {
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.ar,
  },
  fa: {
    about: 'darbare-ma',
    'refund-policy': 'siasat-bazgasht',
    'car-rentals': CAR_RENTAL_LOCALIZED_SLUG.fa,
  },
};

/** Yerelleştirilmiş segment → kanonik İngilizce segment */
const SEGMENT_TO_ENGLISH = {};
Object.values(LOCALIZED_SLUG).forEach((map) => {
  Object.entries(map).forEach(([enSeg, locSeg]) => {
    SEGMENT_TO_ENGLISH[locSeg] = enSeg;
  });
});

export function getLocaleFromPathname(pathname) {
  const first = pathname.split('/').filter(Boolean)[0];
  if (first && LANG_PREFIX_SET.has(first)) return first;
  return 'en';
}

/** URL’de /tr/… gibi açık locale öneki yoksa null — Layout i18n’i pathname’den ezmesin diye. */
export function getExplicitLocaleFromPathname(pathname) {
  const first = pathname.split('/').filter(Boolean)[0];
  if (first && LANG_PREFIX_SET.has(first)) return first;
  return null;
}

/**
 * Pathname’i İngilizce segment dizisine çevirir (locale öneki atılır, slug’lar normalize edilir).
 */
export function pathnameToEnglishSegments(pathname) {
  const raw = pathname.split('/').filter(Boolean);
  let rest = raw;
  if (raw[0] && LANG_PREFIX_SET.has(raw[0])) {
    rest = raw.slice(1);
  }
  return rest.map((s) => SEGMENT_TO_ENGLISH[s] || s);
}

function localizeSegment(locale, englishSegment) {
  if (locale === 'en') return englishSegment;
  return LOCALIZED_SLUG[locale]?.[englishSegment] || englishSegment;
}

/**
 * Hedef locale için path (origin yok): örn. en + [flights] → /flights ; tr + [flights] → /tr/ucuslar
 */
export function buildPathForLocale(locale, englishSegments) {
  const segs = (englishSegments || []).map((s) => localizeSegment(locale, s));
  if (locale === 'en') {
    if (!segs.length) return '/';
    return `/${segs.join('/')}`;
  }
  if (!segs.length) return `/${locale}`;
  return `/${locale}/${segs.join('/')}`;
}

/** Yerelleştirilmiş statik sayfa slug'ını CMS kanonik slug'ına çevirir (örn. hakkimizda → about). */
export function resolveStaticPageSlug(slug) {
  if (!slug) return slug;
  return SEGMENT_TO_ENGLISH[slug] || slug;
}

/** İç linkleri hedef locale ile önekler (/about → /de/uber-uns). */
export function localizeInternalPath(url, locale) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//') || url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }
  if (!url.startsWith('/')) return url;
  const hashIdx = url.indexOf('#');
  const pathAndQuery = hashIdx >= 0 ? url.slice(0, hashIdx) : url;
  const hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  const qIdx = pathAndQuery.indexOf('?');
  const pathOnly = qIdx >= 0 ? pathAndQuery.slice(0, qIdx) : pathAndQuery;
  const query = qIdx >= 0 ? pathAndQuery.slice(qIdx) : '';
  const eng = pathnameToEnglishSegments(pathOnly);
  return `${buildPathForLocale(locale, eng)}${query}${hash}`;
}

/**
 * App.js’te yalnızca kök path ile tanımlı (/:lang/… karşılığı olmayan) sayfalar.
 * Dil değişince /tr/admin gibi var olmayan URL üretilmesin diye pathname aynı kalır; sadece i18n güncellenir.
 */
const LOCALE_AGNOSTIC_PATH_PREFIXES = [
  '/admin',
  '/dashboard',
  '/orders',
  '/checkout',
  '/account/wishlist',
  '/account/settings',
];

export function shouldPreservePathOnLocaleSwitch(pathname) {
  const path = (pathname || '').split('?')[0] || '';
  if (!path) return false;
  return LOCALE_AGNOSTIC_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/** Checkout is always at /checkout (no /:lang prefix) — see App.js routes. */
export function buildCheckoutPath(subpath = '') {
  if (!subpath) return '/checkout';
  const clean = String(subpath).replace(/^\/+/, '');
  return clean ? `/checkout/${clean}` : '/checkout';
}

/**
 * Dil değiştirici: mevcut pathname + search + hash ile hedef locale path’i.
 */
export function buildUrlForLocaleSwitch(targetLocale, pathname, search = '', hash = '') {
  const pathOnly = (pathname || '').split('?')[0] || '';
  if (shouldPreservePathOnLocaleSwitch(pathOnly)) {
    return `${pathOnly}${search || ''}${hash || ''}`;
  }
  const eng = pathnameToEnglishSegments(pathname);
  const path = buildPathForLocale(targetLocale, eng);
  return `${path}${search || ''}${hash || ''}`;
}

/** Ana sayfa canonical: en → kök URL, diğerleri → /{locale} */
export function canonicalHomeUrl(site, locale) {
  if (!site) return '';
  const base = site.replace(/\/$/, '');
  if (locale === 'en') return base;
  return `${base}/${locale}`;
}

/** WebSite SearchAction hedefi (en’de önek yok) */
export function searchActionTargetUrl(site, locale) {
  if (!site) return '';
  const base = site.replace(/\/$/, '');
  if (locale === 'en') return `${base}/search?q={search_term_string}`;
  return `${base}/${locale}/search?q={search_term_string}`;
}

/**
 * hreflang + x-default link listesi (Helmet için).
 * @returns {Array<{ hreflang: string, href: string }>}
 */
export function buildHreflangAlternateLinks(siteBase, pathname, search = '') {
  if (!siteBase) return [];
  const base = siteBase.replace(/\/$/, '');
  const eng = pathnameToEnglishSegments(pathname);
  const q = search && search.startsWith('?') ? search : search ? `?${search}` : '';

  const paths = HREFLANG_LOCALE_CODES.map((code) => {
    const p = buildPathForLocale(code, eng);
    const pathPart = p === '/' ? '' : p;
    return {
      hreflang: code,
      href: `${base}${pathPart}${q}`,
    };
  });

  const enPath = buildPathForLocale('en', eng);
  const xDefault = {
    hreflang: 'x-default',
    href: `${base}${enPath === '/' ? '' : enPath}${q}`,
  };

  return [...paths, xDefault];
}

/** JSON-LD @language veya inLanguage için BCP47 benzeri kısa kod */
export function schemaInLanguage(locale) {
  const map = { en: 'en', tr: 'tr', de: 'de', fr: 'fr', es: 'es', ar: 'ar', zh: 'zh', it: 'it', ru: 'ru', pt: 'pt', fa: 'fa' };
  return map[locale] || locale || 'en';
}
